import React, { FC, ReactElement, useCallback, useMemo } from 'react'
import { DeviceModal } from '../../../../i18n'
import CLanguageHandler from '../../../NonReact/Languages/LanguageHandler'
import { getDeviceList } from '../../Common/API/ItemsGetters'
import { DataTableColumn } from '../../../ReactRouting/Components/Molecules/DataTable/DataTable'
import { DEVICE_ADMIN_LIST_FIELDS } from '../../../NonReact/constants/ApiConstants'
import { DeviceType, deviceTypeIdString } from '../../../NonReact/constants/DeviceTypes'
import { DeviceListItem, DeviceListResponse } from '../../../NonReact/Types/DeviceListTypes'
import { DeviceMemberRow } from '../Types/Types'
import { MemberTable, MemberSearchOption } from './MemberTable'
import { EMPTY_STRING } from '../../Common/Constants/Separators'
import { isStringNotEmpty } from '../../../NonReact/utils/GlobalFunctions'
import type { RangeEntryWrapper } from '../../Common/Components/Selects/RangeCombo/Types'
import { buildRangeFilterExpression } from '../Utils/buildRangeFilterExpression'

const INITIAL_DNIS_OPTIONS_LIMIT: number = 50
const SEARCH_DNIS_OPTIONS_LIMIT: number = 100

const DNIS_DEVICE_TYPE_FILTER: readonly string[] = Object.freeze([deviceTypeIdString(DeviceType.DnisEntry)])
const RANGE_SEARCH_PATTERN: RegExp = /^\d*[xX]?\d*-\d*[xX]?\d*$/

type Props = {
    viewOnly?: boolean
    filterTelSysID: number
    filterPartitionID: number
    selectedRows: DeviceMemberRow[]
    onRowsChange: (rows: DeviceMemberRow[]) => void
    isRouterMode?: boolean
}

/**
 * Formats a DNIS device list row for the async member search options.
 *
 * @param row - Admin device list item restricted to DNIS entries.
 * @returns Single-line label text.
 */
const formatDnisOptionLabel = (row: DeviceListItem): string => {
    const dnisNumber: string = row.DeviceNumber ?? EMPTY_STRING
    const description: string = row.Description?.trim() ?? EMPTY_STRING

    return isStringNotEmpty(description) ? `${dnisNumber} - ${description}` : dnisNumber
}

/**
 * DNIS associations tab for ACD / Call Queue devices using a paginated table plus async add-search control.
 *
 * @param props - Tab props including TelSys / partition scope and current DNIS rows.
 * @returns DNIS-members management UI.
 */
export const DeviceDnisTab: FC<Props> = ({
    viewOnly,
    filterTelSysID,
    filterPartitionID,
    selectedRows,
    onRowsChange,
    isRouterMode
}: Props): ReactElement => {
    const language: CLanguageHandler = CLanguageHandler.getInstance()

    const columns: Array<DataTableColumn<DeviceMemberRow, keyof DeviceMemberRow>> = useMemo(
        (): Array<DataTableColumn<DeviceMemberRow, keyof DeviceMemberRow>> => [
            {
                field: 'DeviceNumber',
                key: 'DeviceNumber',
                headerName: language.get(DeviceModal.DeviceModal_Members_DnisNumber)
            },
            {
                field: 'Description',
                key: 'Description',
                headerName: language.get(DeviceModal.DeviceModal_Members_Description)
            }
        ],
        [language]
    )

    const loadOptions = useCallback(
        async (searchQuery: string): Promise<Array<MemberSearchOption<DeviceListItem>>> => {
            const isInitialOpenQuery: boolean = !isStringNotEmpty(searchQuery)

            if (!isInitialOpenQuery && searchQuery.length < 2) return []

            const isRangeQuery: boolean = !isInitialOpenQuery && RANGE_SEARCH_PATTERN.test(searchQuery)

            const response: Nullable<DeviceListResponse> = await getDeviceList(
                filterTelSysID,
                filterPartitionID,
                0,
                isInitialOpenQuery ? INITIAL_DNIS_OPTIONS_LIMIT : SEARCH_DNIS_OPTIONS_LIMIT,
                isRangeQuery ? undefined : 'DeviceNumber:ASC',
                isRangeQuery || isInitialOpenQuery ? undefined : searchQuery,
                [...DNIS_DEVICE_TYPE_FILTER],
                isRangeQuery ? searchQuery : undefined,
                isRangeQuery ? undefined : DEVICE_ADMIN_LIST_FIELDS
            )

            return (response?.Data?.Items ?? [])
                .filter((row: DeviceListItem): boolean => isStringNotEmpty(row.DeviceNumber ?? EMPTY_STRING))
                .map((row: DeviceListItem) => ({
                    label: formatDnisOptionLabel(row),
                    value: row.DeviceNumber ?? EMPTY_STRING,
                    original: row
                }))
        },
        [filterPartitionID, filterTelSysID]
    )

    const resolveValidIdentifiers = useCallback(
        async (
            pendingRanges: Nullable<RangeEntryWrapper[]>
        ): Promise<ReadonlyArray<MemberSearchOption<DeviceListItem>>> => {
            const filterExpression: string = buildRangeFilterExpression(pendingRanges)

            if (!filterExpression) return []

            const response: Nullable<DeviceListResponse> = await getDeviceList(
                filterTelSysID,
                filterPartitionID,
                0,
                1000,
                undefined,
                undefined,
                [...DNIS_DEVICE_TYPE_FILTER],
                filterExpression
            )

            return (response?.Data?.Items ?? [])
                .filter((row: DeviceListItem): boolean => isStringNotEmpty(row.DeviceNumber ?? EMPTY_STRING))
                .map(
                    (row: DeviceListItem): MemberSearchOption<DeviceListItem> => ({
                        value: row.DeviceNumber ?? EMPTY_STRING,
                        label: formatDnisOptionLabel(row),
                        original: row
                    })
                )
        },
        [filterPartitionID, filterTelSysID]
    )

    return (
        <div className='device-dnis-tab tab-area'>
            <MemberTable
                label={language.get(DeviceModal.DeviceModal_Tab_DnisMembers)}
                noRowsLabel={language.get(DeviceModal.DeviceModal_Members_NoDnisMembers)}
                searchAddButtonTitle={language.get(DeviceModal.DeviceModal_Members_AddDnisHotkey)}
                removeButtonLabel={language.get(DeviceModal.DeviceModal_Members_Remove)}
                searchLabel={language.get(DeviceModal.DeviceModal_Members_AddDnis)}
                rows={selectedRows}
                onRowsChange={onRowsChange}
                columns={columns}
                getIdentifier={(row: DeviceMemberRow): string => row.DeviceNumber}
                createRowFromOption={(option): DeviceMemberRow => ({
                    key: option.value,
                    DeviceNumber: option.value,
                    Description: option.original?.Description ?? EMPTY_STRING
                })}
                loadOptions={loadOptions}
                viewOnly={viewOnly}
                isRouterMode={isRouterMode}
                resolveValidIdentifiers={resolveValidIdentifiers}
            />
        </div>
    )
}
